/**
 * Upload mode types and interfaces for folder upload optimization
 */

export type UploadMode = 'single' | 'folder' | 'archive';

export interface UploadDecision {
  mode: UploadMode;
  fileCount: number;
  totalSize: number;
  useArchive: boolean;
}

export interface FolderStructure {
  name: string;
  type: 'folder' | 'file';
  children?: FolderStructure[];
  fileCount?: number;
  totalSize?: number;
  size?: number;
  extension?: string;
}

export interface TarManifest {
  version: string;
  timestamp: string;
  fileCount: number;
  totalSize: number;
  folderStructure: FolderStructure;
  fileIndex: FileIndexEntry[];
}

export interface FileIndexEntry {
  path: string;
  size: number;
  type: string;
  lastModified: number;
}

export interface UploadProgress {
  uploadId: string;
  progress: number;
  phase: 'uploading' | 'processing' | 'complete';
  chunksReceived?: number;
  totalChunks?: number;
  filesProcessed?: number;
  totalFiles?: number;
  filesFailed?: number;
}

// =============================================================================
// Parsed Data Upload Types
// =============================================================================

/**
 * Statistics for parsed upload
 */
export interface ParsedDataStats {
  totalDocuments: number;
  totalImages: number;
  totalSheets: number;
  totalFolders: number;
  totalSize?: number;
}

/**
 * Parsed upload file type categories
 */
export const PARSED_UPLOAD_TYPES = {
  archive: ['.zip'],
  markdown: ['.md', '.markdown'],
  assets: ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.tiff'],
  sheets: ['.csv', '.parquet', '.xlsx', '.xls']
};

/**
 * Processing phase for the upload
 */
export type ProcessingPhase = 'idle' | 'initializing' | 'uploading' | 'validating' | 'processing' | 'complete' | 'error' | 'cancelled';

/**
 * File info for uploaded files
 */
export interface WizardUploadFile {
  uid: string;
  name: string;
  file: File;
  size: number;
  type: 'markdown' | 'image' | 'sheet';
  md5?: string;
  status: 'pending' | 'uploading' | 'uploaded' | 'error';
  errorMessage?: string;
  /** Relative path from folder upload (from webkitRelativePath) */
  relativePath?: string;
}

/**
 * Validation error from backend
 */
export interface WizardValidationError {
  code: number;
  message: string;
  file_path?: string;
  suggestion?: string;
}

/**
 * WebSocket progress event for upload processing
 */
export interface ParsedUploadProgressEvent {
  session_id: string;
  phase: string;
  overall_progress: number;
  current_file: string;
  files_processed: number;
  files_total: number;
  folders_created: number;
  documents_created: number;
  assets_uploaded: number;
  assets_deduplicated: number;
  errors: Array<{ code: number; message: string; file_path?: string }>;
  warnings: string[];
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
}

// =============================================================================
// Single-Shot Archive Upload Types
// =============================================================================

/**
 * Response from single-shot archive upload endpoint
 */
export interface ParsedDocumentUploadResponse {
  success: boolean;
  documents_created: number;
  folders_created: number;
  assets_uploaded: number;
  assets_deduplicated: number;
  errors: string[];
  warnings: string[];
  document_ids: string[];
  folder_ids: string[];
}
